(() => {
  // Section previews can load the same asset again. Keep one set of listeners.
  if (window.SwitchBotHeaderMenu) {
    window.SwitchBotHeaderMenu.init();
    return;
  }

  const selectAll = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const desktopMenus = new Map();
  const mobileMenus = new WeakSet();
  const scrollAnimations = new WeakMap();
  const menuTypes = new Set(['normal', 'textAndImg', 'robot', 'text', 'thirdLevel', 'aipet', 'helpSupport']);
  const hoverGroups = {
    normal: {
      option: '.switchbot-custom-menu-level1-menu-item',
      content: '.switchbot-custom-menu-level1-prod-wrap .switchbot-custom-menu-level1-prod-itemwrap',
      activeClass: 'switchbot-custom-menu-level1-menu-item-hover',
      display: true,
    },
    thirdLevel: {
      option: '.switchbot-custom-thirdlLevleMenu-level2-menu-item',
      content: '.switchbot-custom-thirdlLevleMenu-level2-prod-itemwrap',
      activeClass: 'active',
    },
    helpSupport: {
      option: '.switchbot-custom-helpSupport-left-selectable',
      content: '.switchbot-custom-helpSupport-level1-prod-itemwrap',
      activeClass: 'active',
    },
  };

  class DesktopMenu {
    constructor(menu) {
      this.menu = menu;
      this.mask = menu.nextElementSibling;
      this.active = null;
      this.entries = selectAll('.switchbot-custom-menu-level0-item', menu).map((item) => {
        const type = item.dataset.menutype;
        const group = hoverGroups[type === 'textAndImg' ? 'normal' : type];
        const panel = item.querySelector('.switchbot-custom-menu-level1-content');
        const inner = panel && panel.querySelector('.switchbot-custom-menu-level1-innercontent');
        const children = inner ? Array.from(inner.children) : [];
        const leftColumn = children.find((child) => child.matches('.switchbot-custom-menu-level1-menu-wrap'));
        const rightColumn = leftColumn && children.find((child) => child !== leftColumn);
        const scrollColumns = rightColumn ? [leftColumn, rightColumn] : [];

        // Split only layouts with a category column; other menus keep one scroll area.
        if (scrollColumns.length) {
          panel.classList.add('custom-menu-split-scroll');
          scrollColumns.forEach((column) => column.classList.add('custom-menu-scroll-column'));
        }
        const entry = {
          item,
          type,
          group,
          panel,
          inner,
          rightColumn,
          scrollColumns,
          selectedContent: null,
          options: group ? selectAll(group.option, item) : [],
          contents: group ? selectAll(group.content, item) : [],
          products: selectAll('.switchbot-custom-menu-level1-prod-itemwrap', item),
          timer: null,
        };

        item.addEventListener('mouseenter', () => this.open(entry));
        item.addEventListener('mouseleave', () => this.close(entry));
        entry.options.forEach((option) => {
          option.addEventListener('mouseenter', () => {
            clearTimeout(entry.timer);
            entry.timer = setTimeout(() => {
              if (this.active !== entry) return;
              const content = group.display
                ? entry.contents[Number(option.dataset.index)]
                : entry.contents.find((element) => element.dataset.id === option.dataset.id);
              this.select(entry, option, content);
              this.updateHeight();
            }, 300);
          });
          option.addEventListener('mouseleave', () => clearTimeout(entry.timer));
        });
        return entry;
      });

      // Header resizing (for example a logo finishing loading) can move the panel.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => scheduleHeightUpdate());
        this.resizeObserver.observe(menu.closest('.header-wrapper') || menu);
      }
    }

    select(entry, selectedOption, selectedContent) {
      entry.options.forEach((option) => {
        option.classList.toggle(entry.group.activeClass, option === selectedOption);
      });
      entry.contents.forEach((content) => {
        if (entry.group.display) {
          content.style.display = content === selectedContent ? 'block' : 'none';
        } else {
          content.classList.toggle('active', content === selectedContent);
        }
      });
      // A category change starts the complete right-hand content at the top.
      // Keep the left position, and do not reset when hovering the same category again.
      if (selectedContent && selectedContent !== entry.selectedContent && entry.rightColumn) {
        entry.rightColumn.scrollTop = 0;
      }
      entry.selectedContent = selectedContent;
    }

    open(entry) {
      if (!window.matchMedia('(min-width: 992px)').matches) return;
      if (!menuTypes.has(entry.type) && !entry.panel) return;
      if (this.active && this.active !== entry) this.close(this.active);
      this.active = entry;
      entry.item.style.borderBottom = '2px solid #E0393A';
      if (this.mask) this.mask.style.display = 'block';

      if (entry.group) this.select(entry, entry.options[0], entry.contents[0]);
      if (entry.type === 'robot' || entry.type === 'aipet') {
        entry.products.forEach((product) => { product.style.display = 'block'; });
      }
      if (entry.panel) entry.panel.classList.add('menu-level1-content-active');
      this.updateHeight();
      if (entry.panel) entry.panel.scrollTop = 0;
      entry.scrollColumns.forEach((column) => { column.scrollTop = 0; });
    }

    close(entry) {
      clearTimeout(entry.timer);
      entry.item.style.border = 'none';
      if (entry.panel) entry.panel.classList.remove('menu-level1-content-active');
      if (entry.group) this.select(entry, null, null);
      if (entry.type === 'robot' || entry.type === 'aipet') {
        entry.products.forEach((product) => { product.style.display = 'none'; });
      }
      if (this.active === entry) {
        this.active = null;
        if (this.mask) this.mask.style.display = 'none';
      }
    }

    updateHeight() {
      if (!this.active || !this.menu.isConnected) return;
      if (!window.matchMedia('(min-width: 992px)').matches) {
        this.close(this.active);
        return;
      }
      const { panel, inner, scrollColumns } = this.active;
      if (!panel) return;
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.offsetTop + viewport.height : document.documentElement.clientHeight;
      // Use the panel's actual viewport position, not screen resolution or a fixed 640px.
      // CSS overflow-y:auto decides whether a scrollbar is needed, including after image loads.
      const availableHeight = Math.max(0, Math.floor(bottom - panel.getBoundingClientRect().top));
      panel.style.setProperty('--custom-menu-available-height', `${availableHeight}px`);
      if (!scrollColumns.length) return;

      // Preserve the existing spacing, including the normal menu's <ul> margins.
      // Each column gets its own height limit; the outer panel does not scroll.
      const innerStyle = getComputedStyle(inner);
      const innerSpacing = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
        .reduce((total, property) => total + (parseFloat(innerStyle[property]) || 0), 0);
      scrollColumns.forEach((column) => {
        const style = getComputedStyle(column);
        const margins = (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
        const height = Math.max(0, Math.floor(availableHeight - innerSpacing - margins));
        column.style.setProperty('--custom-menu-column-height', `${height}px`);
      });
    }

    destroy() {
      if (this.active) this.close(this.active);
      this.entries.forEach((entry) => clearTimeout(entry.timer));
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
  }

  const siblingPanels = (button, selector) => Array.from(button.parentElement.children)
    .filter((element) => element !== button && element.matches(selector));

  const toggleAccordion = (button, buttonSelector, buttonClass, panelSelector, panelClass) => {
    const opening = !button.classList.contains(buttonClass);
    const buttons = opening
      ? selectAll(buttonSelector, button.parentElement.parentElement)
      : [button];
    buttons.forEach((item) => {
      item.classList.remove(buttonClass);
      siblingPanels(item, panelSelector).forEach((panel) => panel.classList.remove(panelClass));
    });
    if (opening) {
      button.classList.add(buttonClass);
      siblingPanels(button, panelSelector).forEach((panel) => panel.classList.add(panelClass));
    }
    return opening;
  };

  const scrollMobileMenu = (button) => {
    const container = button.closest('.my-mb-custom-menu-config');
    if (!container) return;
    cancelAnimationFrame(scrollAnimations.get(container));
    const startTop = container.scrollTop;
    const targetTop = startTop + button.getBoundingClientRect().top - container.getBoundingClientRect().top - 8;
    const started = performance.now();
    // Match the existing jQuery 'slow' duration (600ms) and swing easing.
    const step = (now) => {
      if (!container.isConnected) return;
      const progress = Math.min((now - started) / 600, 1);
      const eased = (1 - Math.cos(progress * Math.PI)) / 2;
      container.scrollTop = startTop + (targetTop - startTop) * eased;
      if (progress < 1) {
        scrollAnimations.set(container, requestAnimationFrame(step));
      } else {
        scrollAnimations.delete(container);
      }
    };
    scrollAnimations.set(container, requestAnimationFrame(step));
  };

  const handleMobileClick = (event) => {
    const button = event.target.closest('[data-menu-level]');
    if (!button || !event.currentTarget.contains(button)) return;
    const type = button.dataset.menutype;
    if (button.dataset.menuLevel === '0') {
      const opening = toggleAccordion(
        button,
        '.switchbot-custom-menumb-level0-item',
        'menumb-level0-item-active',
        '.switchbot-custom-menumb-level1-content',
        'menumb-level1-content-active'
      );
      if (opening && (type === 'robot' || type === 'aipet')) {
        siblingPanels(button, '.switchbot-custom-menumb-level1-content').forEach((panel) => {
          selectAll('.switchbot-custom-menumb-level1-prod-wrap', panel).forEach((products) => {
            products.classList.add('menumb-level1-prod-wrap-active');
          });
        });
      }
      return;
    }

    const panelSelectors = {
      normal: '.switchbot-custom-menumb-level1-prod-wrap',
      thirdLevel: '.switchbot-custom-thirdLevelMenumb-level1-item-content',
      helpSupport: '.switchbot-custom-helpSupportMenumb-level1-item-content',
    };
    if (!panelSelectors[type]) return;
    toggleAccordion(
      button,
      '.switchbot-custom-menumb-level1-item',
      'menumb-level1-item-active',
      panelSelectors[type],
      type === 'normal' ? 'menumb-level1-prod-wrap-active' : 'active'
    );
    scrollMobileMenu(button);
  };

  const setStyles = (selector, styles) => {
    selectAll(selector).forEach((element) => Object.assign(element.style, styles));
  };

  const setMobileOpen = (opening) => {
    if (opening) setStyles('#mobile-menu-overlay', { zIndex: '-10' });
    setStyles('.my-mb-custom-menu-config', { bottom: opening ? '0' : '100%' });
    setStyles('#switchbotMenuMbBtnOpenId', { display: opening ? 'none' : 'block' });
    setStyles('#switchbotMenuMbBtnCloseId', { display: opening ? 'block' : 'none' });
    setStyles('#launcher', { zIndex: opening ? '-1' : '999998' });
    setStyles('#shopify-section-top-bar, [id$="__my-top-bar"]', { display: opening ? 'none' : 'block' });
    return false;
  };

  // Keep the two entry points used by the existing buttons in sections/header.liquid.
  window.switchbotMenuMbBtnOpenIdClickFn = () => setMobileOpen(true);
  window.switchbotMenuMbBtnCloseIdClickFn = () => setMobileOpen(false);

  let heightFrame = null;
  const scheduleHeightUpdate = () => {
    if (heightFrame !== null) return;
    heightFrame = requestAnimationFrame(() => {
      heightFrame = null;
      desktopMenus.forEach((controller) => controller.updateHeight());
    });
  };

  const init = () => {
    desktopMenus.forEach((controller, menu) => {
      if (menu.isConnected) return;
      controller.destroy();
      desktopMenus.delete(menu);
    });
    selectAll('.switchbot-header-menu').forEach((menu) => {
      if (!desktopMenus.has(menu)) desktopMenus.set(menu, new DesktopMenu(menu));
    });
    selectAll('.switchbot-header-menu-mb').forEach((menu) => {
      if (mobileMenus.has(menu)) return;
      menu.addEventListener('click', handleMobileClick);
      mobileMenus.add(menu);
    });
  };

  window.SwitchBotHeaderMenu = { init };
  window.addEventListener('resize', scheduleHeightUpdate, { passive: true });
  window.addEventListener('scroll', scheduleHeightUpdate, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleHeightUpdate, { passive: true });
    window.visualViewport.addEventListener('scroll', scheduleHeightUpdate, { passive: true });
  }
  document.addEventListener('transitionend', (event) => {
    if (event.target.closest('.section-header')) scheduleHeightUpdate();
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    desktopMenus.forEach((controller, menu) => {
      if (!event.target.contains(menu)) return;
      controller.destroy();
      desktopMenus.delete(menu);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
